/**
 * AI Event Handlers
 *
 * This file contains the actual handler implementations for AI events.
 * Handlers are registered with the EventDispatcher and execute when events are dispatched.
 *
 * Current Handlers:
 * - handlePhaseChange: Detects consultation phases and triggers automatic clan councils
 * - handleConversationEnd: Triggers reflection for conversation participants
 *
 * Note: Some handlers are stubs (console logs only) until their services are implemented.
 */

import { supabase } from '../../../lib/supabase'
import { EventType } from './index'
import { logger } from '@/lib/logger'
import type {
  PhaseChangeEvent,
  ConversationEndEvent,
  VoteResultsAnnouncedEvent,
  KingDecisionAnnouncedEvent,
  EventHandler
} from './index'

// In-memory guard against TOCTOU race in dedup check.
// When multiple CONVERSATION_END events fire for the same meetingId near-simultaneously,
// the async DB dedup query may not yet see the inserts from the first call.
// This Set provides a synchronous "first-writer-wins" guard.
const _processedMeetingIds = new Set<string>()

/**
 * Handle Phase Change Events
 *
 * Responsibilities:
 * - Detect if phase requires automatic AI actions
 * - Start clan councils for consultation phases
 * - Log phase transitions for debugging
 */
export const handlePhaseChange: EventHandler<PhaseChangeEvent> = async (event) => {
  logger.log('\n🎯 [PHASE_CHANGE_HANDLER] Processing phase change...')
  logger.log(`🎯 Phase: ${event.previousPhaseName || 'START'} → ${event.phaseName}`)
  logger.log(`🎯 Run ID: ${event.runId}`)

  // Check if this phase requires automatic clan councils
  const requiresClanCouncil = detectClanCouncilPhase(event.phaseName)

  if (requiresClanCouncil) {
    logger.log(`🎯 ⚡ CLAN COUNCIL REQUIRED for phase: ${event.phaseName}`)
    logger.log(`🎯 TODO: Start automatic clan councils (ConversationManager not yet implemented)`)

    // Get all clans in this simulation
    const { data: clans, error } = await supabase
      .from('clans')
      .select('clan_id, name')
      .eq('run_id', event.runId)

    if (error) {
      logger.error('🎯 ❌ Error fetching clans:', error)
      return
    }

    logger.log(`🎯 Found ${clans.length} clans:`)
    clans.forEach(clan => {
      logger.log(`   - ${clan.name} (${clan.clan_id})`)
    })

    // For each clan, we would:
    // 1. Get all AI participants in that clan
    // 2. Start a ConversationManager for that clan
    // 3. Set AI status to 'busy' with 'Clan Council' activity
    logger.log(`🎯 Would start ${clans.length} clan council conversations here`)
    logger.log(`🎯 (ConversationManager will be implemented in Days 2-3)`)

  } else {
    logger.log(`🎯 No automatic actions required for phase: ${event.phaseName}`)
  }

  logger.log('🎯 [PHASE_CHANGE_HANDLER] Complete\n')
}

/**
 * Handle Conversation End Events
 *
 * UPDATED 2025-11-10: Queue updates instead of direct performReflection() calls.
 *
 * Flow:
 * 1. Gather context (phase, time remaining)
 * 2. Build transcript with context
 * 3. Add updates to ai_update_queue for all participants
 * 4. Queue processor handles reflections within 5 seconds
 *
 * Benefits:
 * - All updates visible in ai_update_queue table
 * - No hidden/direct reflections
 * - Single processing path through queue
 * - No double reflections
 */
export const handleConversationEnd: EventHandler<ConversationEndEvent> = async (event) => {
  logger.log(`\n🧠 [CONVERSATION_END] Type: "${event.conversationType}", Participants: ${event.participants.length}, Messages: ${event.transcript.length}`)

  // CRITICAL: Only queue reflections if this event originated from THIS tab
  // If received via BroadcastChannel from another tab (_isOriginatingTab is undefined/false),
  // skip queueing to prevent duplicate reflections when multiple tabs are open
  if (!event._isOriginatingTab) {
    logger.log(`🧠 ⏭️ [CONVERSATION_END] Event received via BroadcastChannel - skipping queue (UI updates only)`)
    return
  }

  logger.log(`🧠 ✅ [CONVERSATION_END] Event originated from this tab - processing queue`)

  // NOTE: public_speech is now handled here (broadcasts to ALL AIs via identifyReflectionParticipants)
  // Database trigger updated to skip ALL meeting types to prevent duplicate queue items

  // Synchronous dedup: prevents TOCTOU race when multiple dispatches fire near-simultaneously.
  // The async DB check below can miss concurrent inserts; this in-memory Set catches them instantly.
  if (_processedMeetingIds.has(event.meetingId)) {
    logger.log(`🧠 ⏭️ [CONVERSATION_END] Meeting ${event.meetingId.slice(0, 8)} already claimed by in-memory guard — skipping`)
    return
  }
  _processedMeetingIds.add(event.meetingId)

  try {
    // Dedup: check if queue entries already exist for this specific meeting (DB-level check)
    const { supabaseBackend } = await import('../../../lib/supabase-backend')
    const meetingTag = `[meeting:${event.meetingId}]`
    const { data: existingItems } = await supabaseBackend
      .from('ai_update_queue')
      .select('queue_id')
      .eq('run_id', event.runId)
      .like('update_content', `%${meetingTag}%`)
      .is('processed_at', null)
      .limit(1)

    if (existingItems && existingItems.length > 0) {
      logger.log(`🧠 ⏭️ [CONVERSATION_END] Updates already pending for meeting ${event.meetingId.slice(0, 8)} — skipping duplicate`)
      return
    }

    // Step 1: Gather simulation context (phase, time remaining)
    const simContext = await gatherSimulationContext(event.runId)
    logger.log(`🧠 Phase: ${simContext.phaseName}`)
    logger.log(`🧠 Conversation Type: ${event.conversationType}`)

    // Step 2: Identify participants who need updates
    const participantsToUpdate = await identifyReflectionParticipants(
      event.conversationType,
      event.participants,
      event.runId
    )

    logger.log(`🧠 Queuing updates for ${participantsToUpdate.length} participant(s)`)

    // Step 3: Build rich context with transcript, phase info, meeting type
    const rawContext = buildRichReflectionContext(
      event.transcript,
      event.conversationType,
      simContext
    )
    // Embed meeting ID tag for dedup detection
    const richContext = `${meetingTag}\n${rawContext}`

    // Step 4: Add updates to queue for all participants
    // Extract unique speaker names for descriptive update_type
    // Filter out system/meta names that aren't actual speakers
    const speakerNames = [...new Set(
      event.transcript
        .map(m => m.speakerName)
        .filter(name => name !== 'System' && name !== 'Public Audience' && name !== 'Secretary')
    )]

    const queuePromises = participantsToUpdate.map(roleId =>
      queueUpdateForParticipant(
        roleId,
        event.runId,
        richContext,
        event.conversationType,
        speakerNames
      )
    )

    const results = await Promise.allSettled(queuePromises)

    // Step 5: Log results
    let successCount = 0
    let failureCount = 0

    results.forEach((result, index) => {
      const roleId = participantsToUpdate[index]
      if (result.status === 'fulfilled') {
        logger.log(`🧠 ✅ Queued update for: ${roleId}`)
        successCount++
      } else {
        logger.error(`🧠 ❌ Failed to queue for: ${roleId}`, result.reason)
        failureCount++
      }
    })

    logger.log(`\n🧠 [CONVERSATION_END_HANDLER] Complete`)
    logger.log(`🧠 Queued: ${successCount}/${participantsToUpdate.length}`)
    logger.log(`🧠 ℹ️  Auto-processor will process within 5 seconds (if enabled)`)
    if (failureCount > 0) {
      logger.warn(`🧠 ⚠️  Failures: ${failureCount}/${participantsToUpdate.length}`)
    }

  } catch (error) {
    logger.error('🧠 ❌ [CONVERSATION_END_HANDLER] Fatal error:', error)
  }
}

/**
 * Handle Vote Results Announced Events
 *
 * Triggered when vote results are revealed (clan nominations, elections, etc.)
 *
 * Responsibilities:
 * - Queue cognitive updates for ALL AI participants
 * - Provide rich context: winner, vote counts, significance
 * - Handle all vote types: nominations, elections, allegiances
 */
export const handleVoteResultsAnnounced: EventHandler<VoteResultsAnnouncedEvent> = async (event) => {
  logger.log('\n🗳️ [VOTE_RESULTS_HANDLER] Processing vote results announcement...')
  logger.log(`🗳️ Vote Type: ${event.voteType}`)
  logger.log(`🗳️ Session: ${event.title}`)
  logger.log(`🗳️ Winner: ${event.winnerRoleName || 'N/A'}`)
  logger.log(`🗳️ Total Votes: ${event.totalVotes}`)

  // Only queue updates from the originating tab (prevent duplicates in multi-tab)
  if (!event._isOriginatingTab) {
    logger.log('🗳️ ⏭️ Event received via BroadcastChannel — skipping queue (UI updates only)')
    return
  }

  try {
    // Dedup: check if entries already exist for this specific vote session
    const sessionTag = `[session:${event.sessionId}]`
    const { data: existingItems } = await supabase
      .from('ai_update_queue')
      .select('queue_id')
      .eq('run_id', event.runId)
      .eq('update_type', 'vote_results_announced')
      .like('update_content', `%${sessionTag}%`)
      .is('processed_at', null)
      .limit(1)

    if (existingItems && existingItems.length > 0) {
      logger.log(`🗳️ ⏭️ Vote result updates already pending for session ${event.sessionId.slice(0, 8)} — skipping duplicate`)
      return
    }

    // Fetch clan name if this is a clan-specific vote
    let clanName: string | null = null
    if (event.clanId) {
      const { data: clan } = await supabase
        .from('clans')
        .select('name')
        .eq('clan_id', event.clanId)
        .single()
      clanName = clan?.name || null
    }

    const rd = event.resultsData
    const contextLines: string[] = [
      sessionTag,
      `**Vote Results Announced: ${event.title}**`,
      '',
    ]

    // ── Build outcome-specific summary from results_data ──

    if (event.voteType === 'clan_nomination') {
      // Clan nomination outcome
      if (rd?.winner) {
        contextLines.push(
          `**OUTCOME: ${clanName || 'The Clan'} has nominated ${rd.winner.name} as their candidate for King.**`,
          `${rd.winner.name} received ${rd.winner.vote_count} votes (${rd.winner.percentage}%) out of ${rd.total_votes || event.totalVotes} votes cast by ${rd.total_participants || event.participantCount} clan members.`
        )
      } else if (rd?.no_nominee_won) {
        contextLines.push(
          `**OUTCOME: ${clanName || 'The Clan'} has decided NOT to nominate any candidate for King.**`,
          `The clan members voted against putting forward a nominee. ${rd.no_nominee_votes || 0} members explicitly voted for "No Nominee".`
        )
      } else if (rd?.tie) {
        const tiedNames = rd.tied_candidates?.map((c: any) => c.name).join(' and ') || 'multiple candidates'
        contextLines.push(
          `**OUTCOME: ${clanName || 'The Clan'} nomination vote ended in a tie between ${tiedNames}.**`,
          `No candidate achieved a clear majority. Each tied candidate received ${rd.tied_candidates?.[0]?.vote_count || '?'} votes.`
        )
      } else {
        // Fallback
        contextLines.push(`**OUTCOME: ${clanName || 'The Clan'} nomination vote concluded.** No clear winner.`)
      }
    } else if (event.voteType === 'general_first' || event.voteType === 'general_second' || event.voteType === 'election_round') {
      // Election outcome
      if (rd?.threshold_met && rd?.winner) {
        contextLines.push(
          `**OUTCOME: ${rd.winner.name} has been elected King!**`,
          `${rd.winner.name} received ${rd.winner.vote_count} votes (${rd.winner.percentage}%), exceeding the required threshold of ${rd.threshold_required} votes.`
        )
      } else if (rd?.runoff_candidates && rd.runoff_candidates.length > 0) {
        const runoffNames = rd.runoff_candidates.map((c: any) => `${c.name} (${c.vote_count} votes)`).join(', ')
        contextLines.push(
          `**OUTCOME: No candidate reached the threshold of ${rd.threshold_required} votes. A runoff vote is required.**`,
          `The following candidates advance to the runoff: ${runoffNames}.`
        )
      } else {
        contextLines.push(
          `**OUTCOME: No candidate reached the required threshold of ${rd?.threshold_required || '?'} votes. The city remains without a King.**`
        )
      }
      if (rd?.abstain_votes) {
        contextLines.push(`${rd.abstain_votes} participants abstained from voting.`)
      }
    } else {
      // Generic fallback for other vote types
      contextLines.push(`Vote Type: ${event.voteType}`, `Format: ${event.voteFormat}`)
      if (event.winnerRoleName) {
        contextLines.push(`Winner: ${event.winnerRoleName}`)
      }
    }

    // ── Vote breakdown (supplementary detail) ──
    if (event.voteBreakdown && event.voteBreakdown.length > 0) {
      contextLines.push('', '**Vote Breakdown:**')
      event.voteBreakdown.forEach(candidate => {
        contextLines.push(`- ${candidate.roleName}: ${candidate.voteCount} votes (${candidate.percentage.toFixed(1)}%)`)
      })
    }

    contextLines.push('', `Total votes cast: ${rd?.total_votes || event.totalVotes}. Eligible voters: ${rd?.total_participants || event.participantCount}.`)

    const updateContent = contextLines.join('\n')

    logger.log(`🗳️ Context built (${updateContent.length} chars)`)

    // Queue updates for ALL AI participants in this run
    const { data, error } = await supabase.rpc('queue_ai_update_for_run', {
      p_run_id: event.runId,
      p_update_type: 'vote_results_announced',
      p_update_content: updateContent,
      p_priority: 1 // High priority - important political event
    })

    if (error) {
      logger.error('🗳️ ❌ Error queuing vote result updates:', error.message)
      throw error
    }

    // Verify items were actually created
    const { data: queueCheck, error: checkError } = await supabase
      .from('ai_update_queue')
      .select('queue_id', { count: 'exact' })
      .eq('run_id', event.runId)
      .eq('update_type', 'vote_results_announced')
      .is('processed_at', null)

    const itemCount = queueCheck?.length ?? 0
    if (itemCount === 0) {
      logger.warn('🗳️ ⚠️ RPC returned no error but 0 queue items were created! Check if AI participants have status=active.')
    } else {
      logger.log(`🗳️ ✅ Queued ${itemCount} vote result update(s) for AI participants`)
    }

    if (checkError) {
      logger.warn('🗳️ ⚠️ Could not verify queue items:', checkError.message)
    }

    logger.log(`🗳️ ℹ️  Auto-processor will process within 5 seconds (if enabled)`)

  } catch (error) {
    logger.error('🗳️ ❌ [VOTE_RESULTS_HANDLER] Fatal error:', error)
  }
}

/**
 * Handle King Decision Announced Events
 *
 * Triggered when the facilitator reveals King's decisions to all participants.
 *
 * Responsibilities:
 * - Fetch full decision data from DB
 * - Build rich context with taxes, appointments, budget, international affairs
 * - Queue cognitive updates for ALL AI participants
 */
export const handleKingDecisionAnnounced: EventHandler<KingDecisionAnnouncedEvent> = async (event) => {
  logger.log('\n👑 [KING_DECISION_HANDLER] Processing King decision announcement...')
  logger.log(`👑 Decision ID: ${event.decisionId}`)

  if (!event._isOriginatingTab) {
    logger.log('👑 ⏭️ Event received via BroadcastChannel — skipping queue (UI updates only)')
    return
  }

  try {
    // Dedup: check if entries already exist for this decision
    const decisionTag = `[decision:${event.decisionId}]`
    const { data: existingItems } = await supabase
      .from('ai_update_queue')
      .select('queue_id')
      .eq('run_id', event.runId)
      .eq('update_type', 'king_decision_announced')
      .like('update_content', `%${decisionTag}%`)
      .is('processed_at', null)
      .limit(1)

    if (existingItems && existingItems.length > 0) {
      logger.log(`👑 ⏭️ King decision updates already pending — skipping duplicate`)
      return
    }

    // Fetch full decision data
    const { data: decision, error: decisionError } = await supabase
      .from('king_decisions')
      .select('*')
      .eq('decision_id', event.decisionId)
      .single()

    if (decisionError || !decision) {
      logger.error('👑 ❌ Failed to fetch king decision:', decisionError?.message)
      return
    }

    // Fetch King's name
    const { data: kingRole } = await supabase
      .from('roles')
      .select('name, position')
      .eq('role_id', decision.king_role_id)
      .single()

    const kingName = kingRole?.name || 'The King'

    // Fetch appointed role names
    const appointments = decision.appointments as any
    let economicAdvisorName = 'Not appointed'
    let seniorJudgeName = 'Not appointed'

    if (appointments?.economic_advisor) {
      const { data: advisor } = await supabase
        .from('roles')
        .select('name, position')
        .eq('role_id', appointments.economic_advisor)
        .single()
      economicAdvisorName = advisor ? `${advisor.name} (${advisor.position})` : 'Unknown'
    }

    if (appointments?.senior_judge) {
      const { data: judge } = await supabase
        .from('roles')
        .select('name, position')
        .eq('role_id', appointments.senior_judge)
        .single()
      seniorJudgeName = judge ? `${judge.name} (${judge.position})` : 'Unknown'
    }

    // Build rich context
    const taxes = decision.taxes as any
    const budget = decision.budget_priorities as any
    const intlAffairs = decision.international_affairs as any

    const contextLines: string[] = [
      decisionTag,
      `**${kingName} has announced the Royal Decisions!**`,
      '',
      '**TAX POLICY:**',
      `- Agriculture: ${taxes?.agriculture || 'same'}`,
      `- Trade: ${taxes?.trade || 'same'}`,
      `- Banking: ${taxes?.banking || 'same'}`,
      `- Craft: ${taxes?.craft || 'same'}`,
      '',
      '**BUDGET PRIORITIES:**',
      `1. ${budget?.priority_1?.replace('_', ' ') || 'Not set'}`,
      `2. ${budget?.priority_2?.replace('_', ' ') || 'Not set'}`,
      `3. ${budget?.priority_3?.replace('_', ' ') || 'Not set'}`,
      '',
      '**KEY APPOINTMENTS:**',
      `- Economic Advisor: ${economicAdvisorName}`,
      `- Senior Judge: ${seniorJudgeName}`,
      '',
      '**INTERNATIONAL AFFAIRS:**',
    ]

    if (intlAffairs?.alliance && intlAffairs.alliance !== 'none') {
      contextLines.push(`- Alliance with ${intlAffairs.alliance}`)
    } else {
      contextLines.push('- No alliance formed')
    }

    if (intlAffairs?.war_declarations?.length > 0) {
      contextLines.push(`- War declared against: ${intlAffairs.war_declarations.join(', ')}`)
    } else {
      contextLines.push('- No wars declared')
    }

    if (decision.other_decisions) {
      contextLines.push('', '**OTHER DECISIONS:**', decision.other_decisions)
    }

    const updateContent = contextLines.join('\n')
    logger.log(`👑 Context built (${updateContent.length} chars)`)

    // Queue updates for ALL AI participants
    const { error } = await supabase.rpc('queue_ai_update_for_run', {
      p_run_id: event.runId,
      p_update_type: 'king_decision_announced',
      p_update_content: updateContent,
      p_priority: 1
    })

    if (error) {
      logger.error('👑 ❌ Error queuing king decision updates:', error.message)
      throw error
    }

    // Verify items were created
    const { data: queueCheck } = await supabase
      .from('ai_update_queue')
      .select('queue_id', { count: 'exact' })
      .eq('run_id', event.runId)
      .eq('update_type', 'king_decision_announced')
      .is('processed_at', null)

    const itemCount = queueCheck?.length ?? 0
    if (itemCount === 0) {
      logger.warn('👑 ⚠️ RPC returned no error but 0 queue items were created!')
    } else {
      logger.log(`👑 ✅ Queued ${itemCount} king decision update(s) for AI participants`)
    }

    logger.log('👑 ℹ️  Auto-processor will process within 5 seconds (if enabled)')

  } catch (error) {
    logger.error('👑 ❌ [KING_DECISION_HANDLER] Fatal error:', error)
  }
}

/**
 * Gather simulation context (phase, time remaining, etc.)
 */
async function gatherSimulationContext(runId: string): Promise<{
  phaseName: string
  phaseDescription: string | null
  timeRemaining: string | null
  phaseId: string | null
}> {
  try {
    const { data: run } = await supabase
      .from('sim_runs')
      .select('current_phase_id')
      .eq('run_id', runId)
      .single()

    if (!run?.current_phase_id) {
      return {
        phaseName: 'Unknown Phase',
        phaseDescription: null,
        timeRemaining: null,
        phaseId: null
      }
    }

    const { data: phase } = await supabase
      .from('phases')
      .select('phase_id, name, description, default_duration_minutes')
      .eq('phase_id', run.current_phase_id)
      .single()

    if (!phase) {
      return {
        phaseName: 'Unknown Phase',
        phaseDescription: null,
        timeRemaining: null,
        phaseId: run.current_phase_id
      }
    }

    const timeRemaining = phase.default_duration_minutes
      ? `${phase.default_duration_minutes} minutes`
      : null

    return {
      phaseName: phase.name,
      phaseDescription: phase.description,
      timeRemaining,
      phaseId: phase.phase_id
    }

  } catch (error) {
    logger.error('🧠 ⚠️  Error gathering sim context:', error)
    return {
      phaseName: 'Unknown Phase',
      phaseDescription: null,
      timeRemaining: null,
      phaseId: null
    }
  }
}

/**
 * Build rich reflection context with all available information
 */
function buildRichReflectionContext(
  transcript: Array<{ speakerName: string; message: string; timestamp: Date }>,
  conversationType: string,
  simContext: {
    phaseName: string
    phaseDescription: string | null
    timeRemaining: string | null
  }
): string {
  const contextType = conversationType === 'public_speech' ? 'PUBLIC SPEECH' : 'CONVERSATION'

  const formattedTranscript = transcript
    .map(msg => {
      const time = msg.timestamp.toLocaleTimeString()
      return `[${time}] ${msg.speakerName}: ${msg.message}`
    })
    .join('\n\n')

  return `# ${contextType} TRANSCRIPT

${formattedTranscript}

---

# SIMULATION CONTEXT

**Current Phase:** ${simContext.phaseName}
${simContext.phaseDescription ? `**Phase Description:** ${simContext.phaseDescription}` : ''}
${simContext.timeRemaining ? `**Time Remaining:** ${simContext.timeRemaining}` : ''}
**Meeting Type:** ${conversationType}`
}

/**
 * Identify which participants need reflection based on conversation type
 */
async function identifyReflectionParticipants(
  conversationType: string,
  eventParticipants: string[],
  runId: string
): Promise<string[]> {
  if (
    conversationType === 'clan_council' ||
    conversationType === 'free_consultation' ||
    conversationType === 'strategic_meeting'
  ) {
    logger.log(`🧠 Text conversation - all ${eventParticipants.length} participants will be queued`)
    return eventParticipants
  }

  if (conversationType === 'voice_conversation') {
    logger.log(`🧠 Voice conversation - AI participant will be queued`)
    return eventParticipants
  }

  if (conversationType === 'public_speech') {
    logger.log(`🧠 Public speech - querying ALL AI participants in run`)

    const { data: allAIs, error } = await supabase
      .from('roles')
      .select('role_id')
      .eq('run_id', runId)
      .eq('participant_type', 'ai')  // FIXED: Database uses participant_type, not is_ai

    if (error) {
      logger.error('🧠 ❌ Error querying AI participants:', error)
      return []
    }

    logger.log(`🧠 ✅ Found ${allAIs?.length || 0} AI participants - all will be queued`)
    return allAIs.map(ai => ai.role_id)
  }

  logger.warn(`🧠 ❌ Unknown conversation type: "${conversationType}"`)
  return []
}

/**
 * Queue an update for a single participant
 * Adds to ai_update_queue table (Priority 1 - immediate)
 */
async function queueUpdateForParticipant(
  roleId: string,
  runId: string,
  richContext: string,
  conversationType: string,
  speakerNames?: string[]
): Promise<void> {
  const { supabaseBackend } = await import('../../../lib/supabase-backend')

  // Build descriptive update_type based on conversation type and participants
  let updateType = 'conversation_ended'
  if (conversationType === 'clan_council') {
    updateType = 'clan_council_ended'
  } else if (conversationType === 'public_speech') {
    const speaker = speakerNames?.[0]
    updateType = speaker ? `public_speech_by_${speaker}` : 'public_speech_ended'
  } else if (conversationType === 'free_consultation') {
    const names = speakerNames?.join(' & ')
    updateType = names ? `conversation_${names}` : 'free_consultation_ended'
  } else if (conversationType === 'voice_conversation') {
    const names = speakerNames?.join(' & ')
    updateType = names ? `voice_conversation_${names}` : 'voice_conversation_ended'
  }

  const { error } = await supabaseBackend
    .from('ai_update_queue')
    .insert({
      role_id: roleId,
      run_id: runId,
      update_type: updateType,
      update_content: richContext,
      priority: 1 // Immediate processing
    })

  if (error) {
    logger.error(`🧠 ❌ Failed to queue update for ${roleId}:`, error.message)
    throw error
  }
}

/**
 * Detect if a phase requires automatic clan councils
 *
 * According to KINGAI_STRATEGY.md, these phases require clan councils:
 * - Phase 4: Free Consultations (first consultation)
 * - Phase 9: Free Consultations (second consultation)
 * - Phase 14: Free Consultations (third consultation)
 *
 * We detect by checking if phase name includes both "free" and "consultation"
 * (case-insensitive)
 */
function detectClanCouncilPhase(phaseName: string): boolean {
  const name = phaseName.toLowerCase()

  // Check for consultation phases
  const isConsultationPhase = name.includes('free') && name.includes('consultation')

  // Alternative detection: Check for "clan council" explicitly
  const isClanCouncilPhase = name.includes('clan') && name.includes('council')

  return isConsultationPhase || isClanCouncilPhase
}

/**
 * Initialize all handlers
 *
 * Call this function once at app startup to register all handlers
 * with the EventDispatcher.
 */
export function initializeEventHandlers(dispatcher: any): void {
  logger.log('\n🎯 ========== INITIALIZING EVENT HANDLERS ==========')

  // Register phase change handler
  dispatcher.registerHandler(EventType.PHASE_CHANGE, handlePhaseChange)
  logger.log('🎯 ✓ Phase change handler registered')

  // Register conversation end handler
  dispatcher.registerHandler(EventType.CONVERSATION_END, handleConversationEnd)
  logger.log('🎯 ✓ Conversation end handler registered')

  // Register vote results handler
  dispatcher.registerHandler(EventType.VOTE_RESULTS_ANNOUNCED, handleVoteResultsAnnounced)
  logger.log('🎯 ✓ Vote results handler registered')

  // Register king decision handler
  dispatcher.registerHandler(EventType.KING_DECISION_ANNOUNCED, handleKingDecisionAnnounced)
  logger.log('🎯 ✓ King decision handler registered')

  logger.log('🎯 ================================================\n')
}
